import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Avatar,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSave, faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons";
import { useDropzone } from "react-dropzone";
import COLORS from "../../../../../constants/colors";
import SnackbarAlert from "../../Reusbale/SnackbarAlert";
import baseURL from "../../config";
import { toast } from "react-toastify";

const UpdateProfileDialog = ({ open, onClose, profile, onSave }) => {
  const [formData, setFormData] = useState({
    name: profile.name || "",
    email: profile.email || "",
    phone: profile.phone || "",
    profile_image: profile.profile_image || "",
    physical_address: profile.physical_address || "",
  });
  

  const [selectedImage, setSelectedImage] = useState(profile.profile_image || "");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Handle Drag & Drop or Click Upload
  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
      setFormData({ ...formData, profile_image: file });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop,
    multiple: false,
  });

 const handleSubmit = async () => {
  let errorMessages = [];

  if (!formData.phone) errorMessages.push("Phone number is required!");
  if (!formData.physical_address) errorMessages.push("Physical address is required!");

  if (errorMessages.length > 0) {
    toast.error(errorMessages.join(" "));
    return;
  }

  const apiFormData = new FormData();
  apiFormData.append("userId", profile.id);
  apiFormData.append("name", formData.name);
  apiFormData.append("email", formData.email);
  apiFormData.append("phone", formData.phone);
  apiFormData.append("profile_image", formData.profile_image);
  apiFormData.append("physical_address", formData.physical_address);

  try {
    const response = await fetch(`${baseURL}/user/update-profile`, {
      method: "POST",
      body: apiFormData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Something went wrong");
    }

    toast.success("Profile updated successfully!");
    onClose();
  } catch (error) {
    toast.error(error.message);
  }
};
  

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: COLORS.primary }}>
          Update Your Details
        </DialogTitle>

        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: COLORS.darkGray,
            fontSize: "1rem",
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Profile Image Upload Section */}
            <Box {...getRootProps()} sx={dropzoneStyles}>
              <input {...getInputProps()} />
              <Avatar
                src={selectedImage}
                sx={{
                  width: 100,
                  height: 100,
                  mb: 1,
                  border: "3px solid #ddd",
                  objectFit: "cover",
                }}
              />
              <Typography variant="body2" color="textSecondary">
                Drag & Drop or{" "}
                <span style={{ color: COLORS.primary, fontWeight: "bold" }}>Click</span> to upload
              </Typography>
              <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" color={COLORS.primary} style={{ marginTop: 5 }} />
            </Box>

            <TextField label="Full Name" name="name" value={formData.name} fullWidth disabled sx={grayedInputStyles} />

            <TextField label="Email Address" name="email" value={formData.email} fullWidth disabled sx={grayedInputStyles} />

            <TextField
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              sx={inputStyles}
            />
            <TextField
  label="Physical Address"
  name="physical_address"
  value={formData.physical_address}
  onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
  fullWidth
  sx={inputStyles}
/>

            
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} sx={cancelButtonStyles}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={saveButtonStyles}>
            <FontAwesomeIcon icon={faSave} style={{ marginRight: 8 }} />
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Error/Success Messages */}
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </>
  );
};

// **Styles**
const dropzoneStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  border: "2px dashed #ccc",
  borderRadius: "10px",
  padding: "20px",
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: "#f9f9f9",
  "&:hover": { borderColor: COLORS.primary, backgroundColor: "#f3f3f3" },
};

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    color: "#666", // Grey text instead of background
  },
};

const grayedInputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    color: "#999", // Light grey text for disabled fields
  },
};

const cancelButtonStyles = {
  textTransform: "none",
  color: COLORS.darkGray,
};

const saveButtonStyles = {
  backgroundColor: COLORS.primary,
  textTransform: "none",
  fontWeight: "bold",
  px: 3,
  py: 1,
  borderRadius: "8px",
  "&:hover": { backgroundColor: COLORS.primary, opacity: 0.9 },
  display: "flex",
  alignItems: "center",
};

export default UpdateProfileDialog;
